import { ActionHintDirective } from '@/app/shared/directives/action-hint.directive';
import { Component, computed, effect, inject, OnDestroy, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Subscription } from 'rxjs';
import { AVATARES, normalizarAvatarId, obtenerRutaAvatar } from '@/app/core/auth/avatar.constants';
import { SesionUsuario } from '@/app/core/auth/auth.models';
import { ContextoTenantStore } from '@/app/core/auth/contexto-tenant.store';
import { SesionService } from '@/app/core/auth/sesion.service';

@Component({
    selector: 'app-avatar-selector',
    imports: [ActionHintDirective, ButtonModule, DialogModule],
    template: `
        <p-dialog
            header="Cambiar avatar"
            closeAriaLabel="Cerrar selector de avatar"
            [visible]="visible()"
            (visibleChange)="!$event && cerrar()"
            (onHide)="cerrar()"
            [modal]="true"
            [dismissableMask]="false"
            [closeOnEscape]="false"
            [draggable]="false"
            [resizable]="false"
            [style]="{ width: '40rem', maxWidth: '95vw' }"
            [breakpoints]="{ '640px': '95vw' }"
            appendTo="body"
         styleClass="app-dialog">
            <p class="avatar-intro">Elige la imagen que aparecerá en tu perfil.</p>
            <fieldset class="avatar-options" [disabled]="guardando()">
                <legend class="avatar-sr-only">Avatares disponibles</legend>
                @for (avatar of avatares; track avatar.id) {
                    <label class="avatar-option" [class.avatar-option-selected]="seleccionado() === avatar.id">
                        <input type="radio" name="avatar-personal" [value]="avatar.id" [attr.aria-label]="avatar.etiqueta" [checked]="seleccionado() === avatar.id" (change)="seleccionar(avatar.id)" />
                        <span class="avatar-card">
                            <img [src]="rutaAvatar(avatar.id)" alt="" aria-hidden="true" width="80" height="80" />
                            @if (seleccionado() === avatar.id) {
                                <i class="pi pi-check avatar-check" aria-hidden="true"></i>
                            }
                        </span>
                    </label>
                }
            </fieldset>
            @if (error()) {
                <p class="avatar-error" role="alert">{{ error() }}</p>
            }
            <ng-template #footer>
                <p-button label="Cancelar" severity="secondary" [outlined]="true" (onClick)="cerrar()"  appActionHint="Cancelar" />
                <p-button label="Guardar avatar" ariaLabel="Guardar avatar" icon="pi pi-check" [loading]="guardando()" [disabled]="guardando() || seleccionado() === avatarActual()" (onClick)="guardar()"  appActionHint="Guardar avatar" />
            </ng-template>
        </p-dialog>
    `,
    styleUrl: './avatar-selector.component.scss'
})
export class AvatarSelectorComponent implements OnDestroy {
    private readonly contexto = inject(ContextoTenantStore);
    private readonly sesion = inject(SesionService);
    private sesionEdicion: SesionUsuario | null = null;
    private guardado?: Subscription;
    readonly avatares = AVATARES;
    readonly rutaAvatar = obtenerRutaAvatar;
    readonly visible = signal(false);
    readonly seleccionado = signal(normalizarAvatarId(undefined));
    readonly guardando = signal(false);
    readonly error = signal('');
    readonly avatarActual = computed(() => normalizarAvatarId(this.contexto.sesion()?.avatar));

    constructor() {
        effect(() => {
            const actual = this.contexto.sesion();
            if (this.visible() && actual !== this.sesionEdicion) this.cerrar();
        });
    }

    abrir(): void {
        this.sesionEdicion = this.contexto.sesion();
        if (!this.sesionEdicion) return;
        this.seleccionado.set(this.avatarActual());
        this.error.set('');
        this.visible.set(true);
    }

    seleccionar(avatar: string): void {
        if (this.guardando()) return;
        this.seleccionado.set(normalizarAvatarId(avatar));
        this.error.set('');
    }

    guardar(): void {
        if (this.guardando() || this.seleccionado() === this.avatarActual() || !this.sesionEdicion || this.sesionEdicion !== this.contexto.sesion()) return;
        this.guardando.set(true);
        this.error.set('');
        this.guardado = this.sesion.guardarAvatar(this.seleccionado()).subscribe({
            next: () => this.cerrar(),
            error: () => {
                this.guardando.set(false);
                this.error.set('No se pudo guardar el avatar. Inténtalo nuevamente.');
            }
        });
    }

    cerrar(): void {
        this.guardado?.unsubscribe();
        this.guardado = undefined;
        this.visible.set(false);
        this.guardando.set(false);
        this.error.set('');
        this.sesionEdicion = null;
    }

    ngOnDestroy(): void {
        this.guardado?.unsubscribe();
    }
}
